import { RadioGroup as RadioGroupPrimitive } from "radix-ui"
import { createContext, type ReactNode, useContext } from "react"

import type { SX } from "../../theme"
import styles from "./RadioGroup.module.css"

export interface RadioGroupProps<V extends string> {
  value: V
  children: ReactNode
  disabled?: boolean
  sx?: SX
  asChild?: boolean
  onChange: (value: V) => void
  id?: string
  name?: string
}

export function RadioGroup<V extends string>(props: RadioGroupProps<V>) {
  const {
    value,
    children,
    disabled = false,
    sx,
    asChild,
    onChange,
    name,
    id,
  } = props

  return (
    <RadioGroupPrimitive.Root
      className={styles.root}
      style={sx}
      disabled={disabled}
      value={value}
      asChild={asChild}
      onValueChange={onChange}
      id={id}
      name={name}
    >
      <RadioGroupContext.Provider value={{ selectedValue: value, disabled }}>
        {children}
      </RadioGroupContext.Provider>
    </RadioGroupPrimitive.Root>
  )
}

interface RadioGroupContextValue {
  selectedValue: string
  disabled: boolean
}

const RadioGroupContext = createContext<RadioGroupContextValue | undefined>(
  undefined,
)

export function useRadioGroup() {
  const context = useContext(RadioGroupContext)
  if (!context)
    throw new Error("useRadioGroup must be used within a RadioGroup")
  return context
}
